import React, { Fragment, useState, useEffect, useMemo, useRef } from 'react';
import {
  FileText,
  Search,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Clock,
  RotateCw,
  Eye,
  RefreshCw,
  ChevronRight,
  ChevronDown,
  Layers,
  AlertCircle,
  ExternalLink,
  ShieldAlert,
  ClipboardList,
} from 'lucide-react';
import { safeFetch } from '../../lib/api';
import { notifyToast } from '../../lib/toastStore';
import { confirmDialog } from '../../lib/confirmStore';
import { mapBulkReviewJobItem } from '../../lib/bulkReviewJobSummary';
import { ListPagination } from './ListPagination';
import {
  DOCUMENT_SELECTION_CAP,
  TAB_SYSTEM_DECISION,
  adminSafeError,
  buildDocumentListQuery,
  buildSelectionJobPayload,
  countAutoApplied,
  describeDocumentEngine,
  describeDocumentVerdict,
  groupSelectionState,
  hasIdentityMismatch,
  isDocumentUndecided,
  isReviewJobActive,
  mapTerminalJobOutcomes,
  normalizeCentreReviewJob,
  reviewItemErrorText,
  selectionEntryOf,
  setGroupDocumentSelection,
  summarizeSelectionOutcomes,
  tabCountFromServerCounts,
  toggleDocumentSelection,
  type CentreReviewJob,
  type DocumentDecisionCounts,
  type DocumentExtractionStamp,
  type DocumentSelection,
  type DocumentSelectionEntry,
  type DocumentVerdictSummary,
  type DocumentsTabKey,
  type SelectionOutcome,
} from '../../lib/documentsCentreModel';

export type { DocumentsTabKey };

export interface AdminDocumentItem {
  id: string;
  docId: string;
  applicationId: string;
  applicantId: string;
  applicantName: string;
  applicantEmail: string;
  program: string;
  programId: string;
  documentType: string;
  documentName: string;
  fileName: string;
  uploadedAt: string;
  uploadedBy: string;
  version: string;
  superseded: boolean;
  supersededBy?: string | null;
  status: string;
  systemDecision: 'System Approved' | 'System Rejected' | 'Pending Review' | 'Further Human Review Required' | 'Processing' | 'Review Failed';
  decisionDetails?: string | null;
  downloadUrl?: string | null;
  storagePath?: string | null;
  judgment?: any;
  aiReview?: any;
  /** PR-A scan record — every field null when never scanned. */
  extraction: DocumentExtractionStamp;
  /** PR-A compact AI verdict summary — fields null when no judgment. */
  verdict: DocumentVerdictSummary;
  reviewLogs?: any[];
  rejectionReasons?: any[];
  fileSize?: number;
  mimeType?: string;
}

export interface AdminDocumentGroup {
  applicationId: string;
  applicantId: string;
  applicantName: string;
  applicantEmail: string;
  program: string;
  programId: string;
  documents: AdminDocumentItem[];
  totalDocuments: number;
  latestUploadedAt: string;
}

interface DocumentsCenterProps {
  onSelectApplication: (appId: string) => void;
  onPreviewDocument: (doc: AdminDocumentItem) => void;
  /**
   * External refresh signal: AdminDashboard increments this after the
   * shared DocumentPreviewModal completes a write (human decision,
   * override, or retry), so the modal outcome visibly lands in the
   * list. Optional — surfaces that never open the modal need not pass
   * it, and increments are harmless when the centre is not mounted.
   */
  refreshSignal?: number;
}

interface SubmitSummaryState {
  jobId: string | null;
  headline: string;
  skipped: Array<SelectionOutcome & { documentName: string }>;
  alreadyRunning: Array<SelectionOutcome & { documentName: string }>;
}

const JOB_STATUS_CHIP: Record<string, { label: string; classes: string }> = {
  queued: { label: 'Queued', classes: 'bg-blue-50 text-blue-700 border-blue-200' },
  validating: { label: 'Validating', classes: 'bg-blue-50 text-blue-700 border-blue-200' },
  processing: { label: 'Running', classes: 'bg-blue-50 text-blue-700 border-blue-200' },
  completed: { label: 'Completed', classes: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  completed_with_errors: { label: 'Completed with errors', classes: 'bg-amber-50 text-amber-700 border-amber-200' },
  failed: { label: 'Failed', classes: 'bg-red-50 text-red-700 border-red-200' },
  cancelled: { label: 'Cancelled', classes: 'bg-slate-100 text-slate-600 border-slate-200' },
};

export const DocumentsCenter: React.FC<DocumentsCenterProps> = ({
  onSelectApplication,
  onPreviewDocument,
  refreshSignal,
}) => {
  const [activeTab, setActiveTab] = useState<DocumentsTabKey>('all');
  const [documents, setDocuments] = useState<AdminDocumentItem[]>([]);
  const [groups, setGroups] = useState<AdminDocumentGroup[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<{ safe: string; raw: string } | null>(null);

  // Server pagination + counts (R3)
  const [serverCounts, setServerCounts] = useState<DocumentDecisionCounts | null>(null);
  const [total, setTotal] = useState<number>(0);
  const [totalDocuments, setTotalDocuments] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(25);

  // Search & Filters state (the dead `statusFilter` was removed — it had
  // no UI control; tabs are the systemDecision bucket selector now)
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [debouncedSearch, setDebouncedSearch] = useState<string>('');
  const [programFilter, setProgramFilter] = useState<string>('');
  const [docTypeFilter, setDocTypeFilter] = useState<string>('');
  const [versionFilter, setVersionFilter] = useState<string>('current'); // 'current', 'historical', 'all'
  const [groupByApp, setGroupByApp] = useState<boolean>(false);
  // Filter option lists accumulate across fetched pages (the endpoint
  // has no facets read; a page-local list would shrink as you page).
  const [programOptions, setProgramOptions] = useState<string[]>([]);
  const [docTypeOptions, setDocTypeOptions] = useState<string[]>([]);

  // Selection & bulk review (R1/R2) — id-based, survives paging/views
  const [selection, setSelection] = useState<DocumentSelection>(new Map());
  const [bulkSubmitting, setBulkSubmitting] = useState<boolean>(false);
  const [submitSummary, setSubmitSummary] = useState<SubmitSummaryState | null>(null);

  // Background review jobs started in this session (I2)
  const [sessionJobs, setSessionJobs] = useState<CentreReviewJob[]>([]);
  const [expandedJobId, setExpandedJobId] = useState<string | null>(null);
  const sessionJobsRef = useRef<CentreReviewJob[]>([]);
  const completionToastedRef = useRef<Set<string>>(new Set());

  // Mobile detail expansion
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null);

  // Debounce the search box (~300ms) — every keystroke used to fire a
  // full workspace scan (I3).
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const filtersKey = useMemo(
    () =>
      JSON.stringify({
        s: debouncedSearch,
        p: programFilter,
        d: docTypeFilter,
        v: versionFilter,
        t: activeTab,
        g: groupByApp,
      }),
    [debouncedSearch, programFilter, docTypeFilter, versionFilter, activeTab, groupByApp]
  );
  const lastFiltersKeyRef = useRef<string>('');

  const fetchDocuments = async (opts?: { silent?: boolean }) => {
    if (!opts?.silent) setLoading(true);
    setError(null);
    try {
      const query = buildDocumentListQuery({
        search: debouncedSearch,
        program: programFilter,
        docType: docTypeFilter,
        systemDecision: TAB_SYSTEM_DECISION[activeTab],
        version: versionFilter,
        page,
        pageSize,
        groupByApplication: groupByApp,
      });
      const res = await safeFetch(`/api/admin/documents?${query}`);
      const fetched: AdminDocumentItem[] = Array.isArray(res?.documents) ? res.documents : [];
      setServerCounts(res?.counts || null);
      if (groupByApp) {
        setGroups(Array.isArray(res?.groups) ? res.groups : []);
        setTotal(typeof res?.total === 'number' ? res.total : 0);
        setTotalDocuments(typeof res?.totalDocuments === 'number' ? res.totalDocuments : 0);
        setDocuments(fetched);
      } else {
        setDocuments(fetched);
        setTotal(typeof res?.total === 'number' ? res.total : 0);
        setTotalDocuments(0);
        setGroups([]);
      }
      // Accumulate filter options across pages (union, stable order).
      setProgramOptions(prev => {
        const seen = new Set(prev);
        fetched.forEach(d => { if (d.program) seen.add(d.program); });
        return Array.from(seen);
      });
      setDocTypeOptions(prev => {
        const seen = new Set(prev);
        fetched.forEach(d => { if (d.documentType) seen.add(d.documentType); });
        return Array.from(seen);
      });
    } catch (err: any) {
      console.error('Failed to load admin documents:', err);
      const safe = adminSafeError(err, 'Failed to load documents. Please try again.');
      setError(safe);
    } finally {
      if (!opts?.silent) setLoading(false);
    }
  };

  // Filter/tab/view changes reset to page 1 — handled inside the fetch
  // effect so exactly one request fires (with page=1) per change.
  useEffect(() => {
    if (lastFiltersKeyRef.current !== filtersKey) {
      lastFiltersKeyRef.current = filtersKey;
      if (page !== 1) {
        setPage(1);
        return;
      }
    }
    fetchDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersKey, page, pageSize]);

  // Modal-write landing: when AdminDashboard's refresh signal changes
  // (the shared DocumentPreviewModal completed a decision / override /
  // retry), silently refetch the CURRENT page — same silent path the
  // background-job completion uses — so the outcome is visible in the
  // list without disturbing page, filters, or selection. The ref starts
  // at the incoming value, so the initial mount (already covered by the
  // fetch effect above) never double-fires, and a remount after writes
  // happened elsewhere simply loads fresh data via that same effect.
  const lastRefreshSignalRef = useRef(refreshSignal);
  useEffect(() => {
    if (lastRefreshSignalRef.current === refreshSignal) return;
    lastRefreshSignalRef.current = refreshSignal;
    fetchDocuments({ silent: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshSignal]);

  useEffect(() => {
    sessionJobsRef.current = sessionJobs;
  }, [sessionJobs]);

  // ── Background job polling (ApplicantDetail pattern: ~8s while active)
  const activeJobIdsKey = sessionJobs.filter(j => isReviewJobActive(j.status)).map(j => j.id).join(',');
  useEffect(() => {
    if (!activeJobIdsKey) return;
    const poll = async () => {
      const current = sessionJobsRef.current.filter(j => isReviewJobActive(j.status));
      let anyCompleted = false;
      for (const job of current) {
        try {
          const res = await safeFetch(`/api/admin/documents/bulk-review-job/${encodeURIComponent(job.id)}`);
          const normalized = normalizeCentreReviewJob(res, job);
          if (!normalized) continue;
          const wasActive = isReviewJobActive(job.status);
          setSessionJobs(prev => prev.map(j => (j.id === normalized.id ? normalized : j)));
          if (wasActive && !isReviewJobActive(normalized.status) && !completionToastedRef.current.has(normalized.id)) {
            completionToastedRef.current.add(normalized.id);
            anyCompleted = true;
            const outcomes = mapTerminalJobOutcomes(normalized, mapBulkReviewJobItem);
            const autoApplied = countAutoApplied(outcomes);
            const autoRejected = outcomes.filter(o => o.appliedState === 'auto_applied' && o.decision === 'REJECTED').length;
            const autoApproved = autoApplied - autoRejected;
            notifyToast.info(
              `Background review finished: ${normalized.completedItems}/${normalized.totalItems} reviewed` +
                (autoApproved > 0 ? `, ${autoApproved} approval(s) applied automatically` : '') +
                (autoRejected > 0 ? `, ${autoRejected} rejection(s) applied automatically` : '') +
                (normalized.failedItems > 0 ? `, ${normalized.failedItems} failed` : '') +
                '.',
              'Review job complete'
            );
          }
        } catch (err: any) {
          console.error('Failed to poll review job:', job.id, err);
        }
      }
      if (anyCompleted) fetchDocuments({ silent: true });
    };
    const t = setInterval(poll, 8000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeJobIdsKey]);

  // Mapped terminal outcomes per document (across this session's jobs;
  // the most recently submitted job wins for a document).
  const outcomeByDocId = useMemo(() => {
    const map = new Map<string, ReturnType<typeof mapBulkReviewJobItem>>();
    for (const job of [...sessionJobs].reverse()) {
      const outcomes = mapTerminalJobOutcomes(job, mapBulkReviewJobItem);
      for (const outcome of outcomes) {
        if (outcome) map.set(outcome.docId, outcome);
      }
    }
    return map;
  }, [sessionJobs]);

  const rawItemByDocId = useMemo(() => {
    const map = new Map<string, { status: string; errorText: string | null }>();
    for (const job of [...sessionJobs].reverse()) {
      for (const item of job.items) {
        const docId = item.result?.docId || item.entityId;
        if (!docId) continue;
        map.set(docId, { status: item.status, errorText: reviewItemErrorText(item) });
      }
    }
    return map;
  }, [sessionJobs]);

  // ── Selection handlers (R1) ──────────────────────────────────────
  const noteCapped = (cappedCount: number) => {
    if (cappedCount > 0) {
      notifyToast.warning(
        `Selection is capped at ${DOCUMENT_SELECTION_CAP} documents per review job — ${cappedCount} document(s) were not added.`,
        'Selection limit reached'
      );
    }
  };

  const handleSelectDoc = (doc: AdminDocumentItem) => {
    const { next, capped } = toggleDocumentSelection(selection, doc);
    if (capped) {
      noteCapped(1);
      return;
    }
    setSelection(next);
  };

  const handleSelectPage = (docs: AdminDocumentItem[], select: boolean) => {
    const { next, cappedCount } = setGroupDocumentSelection(selection, docs, select);
    noteCapped(cappedCount);
    setSelection(next);
  };

  // ── One-job submit (R2) ──────────────────────────────────────────
  const registerJobStub = (jobId: string, totalItems: number) => {
    setSessionJobs(prev => {
      if (prev.some(j => j.id === jobId)) {
        return prev.map(j => (j.id === jobId ? { ...j, totalItems: j.totalItems || totalItems } : j));
      }
      const stub = normalizeCentreReviewJob({ jobId, status: 'queued', totalItems });
      return stub ? [stub, ...prev] : prev;
    });
  };

  const submitEntriesForReview = async (entries: DocumentSelectionEntry[]) => {
    if (entries.length === 0 || bulkSubmitting) return;
    setBulkSubmitting(true);
    setSubmitSummary(null);
    try {
      const payload = buildSelectionJobPayload(
        new Map(entries.map(e => [e.docId, e]))
      );
      const res = await safeFetch('/api/admin/documents/bulk-review-job', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const outcomes: SelectionOutcome[] = Array.isArray(res?.outcomes) ? res.outcomes : [];
      const summary = summarizeSelectionOutcomes(outcomes);
      const nameOf = (o: SelectionOutcome) =>
        entries.find(e => e.docId === o.docId)?.documentName || o.docId;
      setSubmitSummary({
        jobId: res?.jobId || null,
        headline: summary.headline,
        skipped: summary.skipped.map(o => ({ ...o, documentName: nameOf(o) })),
        alreadyRunning: summary.alreadyRunning.map(o => ({ ...o, documentName: nameOf(o) })),
      });

      if (res?.jobId) registerJobStub(String(res.jobId), summary.accepted.length);
      // Watch any already-running jobs the server pointed at, too.
      summary.alreadyRunning.forEach(o => {
        if (o.jobId) registerJobStub(String(o.jobId), 0);
      });

      // Accepted + already-running documents leave the selection (they
      // are in a job now); skipped documents stay selected so the
      // admin can see why and act on them.
      setSelection(prev => {
        const next = new Map(prev);
        summary.accepted.forEach(o => next.delete(o.docId));
        summary.alreadyRunning.forEach(o => next.delete(o.docId));
        return next;
      });

      if (summary.accepted.length > 0) {
        notifyToast.success(
          `One background review job started for ${summary.accepted.length} document(s). Approvals that pass every check are applied automatically; everything else will be waiting here.`,
          'Review job started'
        );
      } else if (summary.alreadyRunning.length > 0) {
        notifyToast.info(summary.headline, 'Already being reviewed');
      } else {
        notifyToast.warning(summary.headline, 'Nothing was queued');
      }
    } catch (err: any) {
      console.error('Failed to submit documents for review:', err);
      const safe = adminSafeError(err, 'Could not start the review job. Please try again.');
      notifyToast.error(safe.safe, 'Review job not started');
    } finally {
      setBulkSubmitting(false);
    }
  };

  const handleBulkReviewSubmit = async () => {
    const entries = [...selection.values()];
    if (entries.length === 0) return;
    const confirmed = await confirmDialog(
      `Submit ${entries.length} document(s) for system review? They will be reviewed together as ONE background job — you can leave this page while it runs. Approvals that pass every check are applied automatically; everything else stays for your decision.`,
      { confirmLabel: 'Start review', cancelLabel: 'Cancel' }
    );
    if (!confirmed) return;
    await submitEntriesForReview(entries);
  };

  const handleSingleReview = async (doc: AdminDocumentItem) => {
    await submitEntriesForReview([selectionEntryOf(doc)]);
  };

  // ── Render helpers ───────────────────────────────────────────────
  const renderStatusBadge = (decision: AdminDocumentItem['systemDecision']) => {
    switch (decision) {
      case 'System Approved':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
            System Approved
          </span>
        );
      case 'System Rejected':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-full bg-red-50 text-red-700 border border-red-200">
            <XCircle className="w-3.5 h-3.5 text-red-600" />
            System Rejected
          </span>
        );
      case 'Further Human Review Required':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-full bg-amber-50 text-amber-700 border border-amber-200">
            <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />
            Further Human Review Required
          </span>
        );
      case 'Processing':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-full bg-blue-50 text-blue-700 border border-blue-200">
            <RefreshCw className="w-3.5 h-3.5 text-blue-600 animate-spin" />
            Processing
          </span>
        );
      case 'Review Failed':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-full bg-rose-50 text-rose-700 border border-rose-200">
            <ShieldAlert className="w-3.5 h-3.5 text-rose-600" />
            Review Failed
          </span>
        );
      case 'Pending Review':
      default:
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-full bg-slate-100 text-slate-700 border border-slate-200">
            <Clock className="w-3.5 h-3.5 text-slate-500" />
            Pending Review
          </span>
        );
    }
  };

  const VERDICT_TONE_CLASSES: Record<string, string> = {
    emerald: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200',
    red: 'bg-red-50 text-red-700 ring-1 ring-red-200',
    amber: 'bg-amber-50 text-amber-700 ring-1 ring-amber-200',
    slate: 'bg-slate-100 text-slate-600 ring-1 ring-slate-200',
  };

  /** Engine badge + AI verdict chip + scan time (D1) — ApplicantDetail idiom. */
  const renderScanInfo = (doc: AdminDocumentItem) => {
    const engine = describeDocumentEngine(doc.extraction);
    const verdictChip = describeDocumentVerdict(doc.verdict);
    const mismatch = hasIdentityMismatch({ verdict: doc.verdict, judgment: doc.judgment, aiReview: doc.aiReview });
    return (
      <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
        <span
          className="inline-flex items-center px-1.5 py-px rounded-md text-[10px] font-semibold bg-slate-100 text-slate-500 ring-1 ring-slate-200"
          title={engine.title}
        >
          {engine.label}
        </span>
        {verdictChip && (
          <span
            className={`inline-flex items-center px-1.5 py-px rounded-md text-[10px] font-semibold ${VERDICT_TONE_CLASSES[verdictChip.tone]}`}
            title={doc.verdict?.reason || undefined}
          >
            {verdictChip.label}
          </span>
        )}
        {mismatch && (
          <span className="inline-flex items-center px-1.5 py-px rounded-md text-[10px] font-bold bg-red-50 text-red-700 ring-1 ring-red-200">
            Identity mismatch — review individually
          </span>
        )}
        {engine.scanned && doc.extraction?.processedAt && (
          <span className="text-[11px] text-slate-500">
            Scanned {new Date(doc.extraction.processedAt).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' })}
          </span>
        )}
      </div>
    );
  };

  /** Job outcome overlay for a row (R2): applied state or the decide action. */
  const renderJobOutcome = (doc: AdminDocumentItem) => {
    const outcome = outcomeByDocId.get(doc.docId);
    const raw = rawItemByDocId.get(doc.docId);
    if (!outcome) {
      if (raw && raw.status === 'failed') {
        return <div className="text-[11px] font-semibold text-rose-600 mt-1.5">Review failed{raw.errorText ? `: ${raw.errorText}` : ''}</div>;
      }
      return null;
    }
    if (outcome.appliedState === 'auto_applied') {
      if (outcome.decision === 'REJECTED') {
        return (
          <div className="mt-1.5">
            <span
              className="inline-flex items-center px-1.5 py-px rounded-md text-[10px] font-bold bg-red-50 text-red-700 ring-1 ring-red-200"
              title={outcome.reason || undefined}
            >
              Auto-rejected{outcome.reason ? ` — ${outcome.reason}` : ''} · background review
            </span>
          </div>
        );
      }
      return (
        <div className="mt-1.5">
          <span className="inline-flex items-center px-1.5 py-px rounded-md text-[10px] font-bold bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200">
            Auto-approved ✓ · background review
          </span>
        </div>
      );
    }
    if (outcome.appliedState === 'skipped_decided') {
      return (
        <div className="mt-1.5">
          <span className="inline-flex items-center px-1.5 py-px rounded-md text-[10px] font-semibold bg-slate-100 text-slate-600 ring-1 ring-slate-200">
            Decided by a reviewer while the job ran
          </span>
        </div>
      );
    }
    // pending — the admin's decision is still needed
    return (
      <div className="flex flex-wrap items-center gap-2 mt-1.5">
        <span className="text-[11px] font-semibold text-slate-600">
          {outcome.error
            ? `Review could not complete: ${outcome.error}`
            : `Background review: ${outcome.decision === 'APPROVED' ? 'approved by AI — awaiting your confirmation' : outcome.decision === 'REJECTED' ? 'AI proposes rejection — your confirmation required' : `needs your review${outcome.reason ? ` — ${outcome.reason}` : ''}`}`}
        </span>
        <button
          onClick={() => onPreviewDocument(doc)}
          className="px-2 py-0.5 text-[11px] font-bold text-brand bg-brand/10 rounded-lg hover:bg-brand/20 transition-colors cursor-pointer"
        >
          Review &amp; decide
        </button>
      </div>
    );
  };

  const renderRowActions = (doc: AdminDocumentItem) => (
    <>
      {isDocumentUndecided(doc) && (
        <button
          onClick={() => handleSingleReview(doc)}
          disabled={bulkSubmitting}
          className="px-2.5 py-1 text-xs font-semibold text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors cursor-pointer inline-flex items-center gap-1 disabled:opacity-50"
          title="Run the system review for this document in the background"
        >
          <RotateCw className="w-3.5 h-3.5" />
          Review
        </button>
      )}
      <button
        onClick={() => onPreviewDocument(doc)}
        className="px-2.5 py-1 text-xs font-semibold text-brand bg-brand/10 rounded-lg hover:bg-brand/20 transition-colors cursor-pointer inline-flex items-center gap-1"
      >
        <Eye className="w-3.5 h-3.5" />
        Preview
      </button>
    </>
  );

  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const flatStart = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const flatEnd = (page - 1) * pageSize + documents.length;
  const groupStart = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const groupEnd = (page - 1) * pageSize + groups.length;
  const docsOnGroupPage = groups.reduce((sum, g) => sum + g.documents.length, 0);
  const pageSelectionState = groupSelectionState(selection, documents);

  return (
    <div className="space-y-6">
      {/* Header Banner */}
      <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-brand font-semibold text-sm mb-1">
            <FileText className="w-4 h-4" />
            Compliance & Operations
          </div>
          <h1 className="text-2xl font-bold text-slate-900">Documents Management & System Review Centre</h1>
          <p className="text-sm text-slate-500 mt-1">
            Central review workspace for tracking, auditing, and processing applicant documents across all programs.
          </p>
        </div>

        <div className="flex items-center gap-3 shrink-0">
          <button
            onClick={() => fetchDocuments()}
            disabled={loading}
            className="inline-flex items-center gap-2 px-3.5 py-2 text-xs font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh Data
          </button>
        </div>
      </div>

      {/* Tabs — server bucket selectors; counts come from the server */}
      <div className="border-b border-slate-200 bg-white rounded-xl shadow-sm overflow-x-auto">
        <nav className="flex space-x-1 px-4 py-2" aria-label="Tabs">
          {([
            { key: 'all', label: 'All Documents', icon: FileText },
            { key: 'pending', label: 'Pending Review', icon: Clock },
            { key: 'system_approved', label: 'System Approved', icon: CheckCircle2 },
            { key: 'system_rejected', label: 'System Rejected', icon: XCircle },
            { key: 'further_human_review', label: 'Further Human Review Required', icon: AlertTriangle },
            { key: 'review_failed', label: 'Review Failed', icon: ShieldAlert },
          ] as const).map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-2 px-3.5 py-2.5 text-xs font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap ${
                  isActive
                    ? 'bg-brand text-white shadow-sm'
                    : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                <span>{tab.label}</span>
                <span
                  className={`px-2 py-0.5 text-[10px] rounded-full font-bold ${
                    isActive ? 'bg-white/20 text-white' : 'bg-slate-200 text-slate-700'
                  }`}
                >
                  {tabCountFromServerCounts(serverCounts, tab.key)}
                </span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Search & Filters Controls */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm space-y-4">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="relative w-full md:w-96">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search applicant, email, app ID, doc name/type..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2 text-xs border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand"
            />
          </div>

          <div className="flex flex-wrap items-center gap-3 w-full md:w-auto justify-end">
            {/* Program Filter */}
            <select
              value={programFilter}
              onChange={e => setProgramFilter(e.target.value)}
              className="px-3 py-2 text-xs border border-slate-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-brand/20"
              aria-label="Program filter"
            >
              <option value="">All Programs</option>
              {programOptions.map(p => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>

            {/* Document Type Filter */}
            <select
              value={docTypeFilter}
              onChange={e => setDocTypeFilter(e.target.value)}
              className="px-3 py-2 text-xs border border-slate-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-brand/20"
              aria-label="Document type filter"
            >
              <option value="">All Doc Types</option>
              {docTypeOptions.map(t => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>

            {/* Version Filter */}
            <select
              value={versionFilter}
              onChange={e => setVersionFilter(e.target.value)}
              className="px-3 py-2 text-xs border border-slate-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-brand/20"
              aria-label="Version filter"
            >
              <option value="current">Current Version Only</option>
              <option value="historical">Historical Versions</option>
              <option value="all">All Versions</option>
            </select>

            {/* Grouping Toggle */}
            <button
              onClick={() => setGroupByApp(!groupByApp)}
              className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium border rounded-lg transition-colors cursor-pointer ${
                groupByApp ? 'bg-brand/10 border-brand text-brand' : 'bg-white border-slate-300 text-slate-700 hover:bg-slate-50'
              }`}
            >
              <Layers className="w-3.5 h-3.5" />
              <span>Group by Application</span>
            </button>

            {(searchQuery || programFilter || docTypeFilter || versionFilter !== 'current') && (
              <button
                onClick={() => {
                  setSearchQuery('');
                  setProgramFilter('');
                  setDocTypeFilter('');
                  setVersionFilter('current');
                }}
                className="text-xs text-brand font-semibold hover:underline"
              >
                Clear Filters
              </button>
            )}
          </div>
        </div>

        {/* Bulk Action Bar */}
        {selection.size > 0 && (
          <div className="bg-brand/5 border border-brand/20 rounded-lg p-3 flex items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-brand">
                {selection.size} document(s) selected
              </span>
              {selection.size >= DOCUMENT_SELECTION_CAP ? (
                <span className="text-xs font-semibold text-amber-600">(Selection limit reached — {DOCUMENT_SELECTION_CAP} per review job)</span>
              ) : (
                <span className="text-xs text-slate-500">(Up to {DOCUMENT_SELECTION_CAP} per review job, submitted as ONE job)</span>
              )}
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={handleBulkReviewSubmit}
                disabled={bulkSubmitting}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-brand text-white rounded-lg hover:bg-brand/90 transition-colors shadow-sm disabled:opacity-50 cursor-pointer"
              >
                <RotateCw className={`w-3.5 h-3.5 ${bulkSubmitting ? 'animate-spin' : ''}`} />
                Submit for System Review ({selection.size})
              </button>
              <button
                onClick={() => setSelection(new Map())}
                className="text-xs text-slate-600 hover:text-slate-900 px-2 py-1"
              >
                Deselect All
              </button>
            </div>
          </div>
        )}

        {/* Last submission outcome summary — per-item honesty (R2) */}
        {submitSummary && (
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs space-y-1.5">
            <div className="flex items-center justify-between">
              <span className="font-bold text-slate-800">
                Last submission: {submitSummary.headline}
                {submitSummary.jobId ? ` · Job ${submitSummary.jobId}` : ''}
              </span>
              <button onClick={() => setSubmitSummary(null)} className="text-slate-400 hover:text-slate-600 font-bold" aria-label="Dismiss submission summary">
                <XCircle className="w-4 h-4" />
              </button>
            </div>
            {submitSummary.alreadyRunning.map(o => (
              <div key={o.docId} className="text-slate-600">
                • {o.documentName} — already in a running review job.
              </div>
            ))}
            {submitSummary.skipped.map(o => (
              <div key={o.docId} className="text-amber-700">
                • {o.documentName} — skipped{o.reason ? `: ${o.reason}` : ''}.
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Review jobs strip (I2) — jobs started in this session */}
      {sessionJobs.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
            <h2 className="text-sm font-bold text-slate-900 flex items-center gap-2">
              <ClipboardList className="w-4 h-4 text-brand" />
              Review jobs
            </h2>
            <span className="text-[11px] text-slate-500">Jobs started in this session</span>
          </div>
          <div className="divide-y divide-slate-100">
            {sessionJobs.map(job => {
              const chip = JOB_STATUS_CHIP[job.status] || { label: job.status, classes: 'bg-slate-100 text-slate-600 border-slate-200' };
              const outcomes = mapTerminalJobOutcomes(job, mapBulkReviewJobItem);
              const autoApplied = countAutoApplied(outcomes);
              const autoRejectedCount = outcomes.filter(o => o.appliedState === 'auto_applied' && o.decision === 'REJECTED').length;
              const autoApprovedCount = autoApplied - autoRejectedCount;
              const expanded = expandedJobId === job.id;
              return (
                <div key={job.id} className="px-4 py-3">
                  <div className="flex flex-wrap items-center gap-3">
                    <span className={`inline-flex items-center px-2 py-0.5 text-[11px] font-bold rounded-full border ${chip.classes}`}>
                      {chip.label}
                    </span>
                    <span className="text-xs font-semibold text-slate-800">
                      {job.completedItems}/{job.totalItems} reviewed
                      {job.failedItems > 0 ? ` · ${job.failedItems} failed` : ''}
                      {autoApprovedCount > 0 ? ` · ${autoApprovedCount} auto-approved` : ''}
                      {autoRejectedCount > 0 ? ` · ${autoRejectedCount} auto-rejected` : ''}
                    </span>
                    <div className="flex-1 min-w-[120px] max-w-xs h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-brand rounded-full transition-all"
                        style={{ width: `${Math.min(100, Math.max(0, job.progressPercent))}%` }}
                      />
                    </div>
                    <span className="text-[11px] text-slate-500">
                      {job.createdAt ? new Date(job.createdAt).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' }) : ''}
                      {job.createdByName ? ` · ${job.createdByName}` : ''}
                    </span>
                    <span className="font-mono text-[11px] text-slate-500" title="Review job ID">{job.id}</span>
                    <button
                      onClick={() => setExpandedJobId(expanded ? null : job.id)}
                      className="text-xs font-semibold text-brand hover:underline inline-flex items-center gap-1 cursor-pointer"
                    >
                      {expanded ? 'Hide items' : 'View items'}
                      {expanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                    </button>
                  </div>
                  {expanded && (
                    <div className="mt-3 space-y-1.5">
                      {job.items.length === 0 ? (
                        <p className="text-xs text-slate-500">No item outcomes yet — the job is still running.</p>
                      ) : (
                        job.items.map(item => {
                          const outcome = outcomes.find(o => o.docId === (item.result?.docId || item.entityId));
                          const docName = item.result?.docName || item.docName || item.entityId || 'Document';
                          return (
                            <div key={item.id} className="flex items-start justify-between gap-3 text-xs bg-slate-50 border border-slate-100 rounded-lg px-3 py-2">
                              <div className="min-w-0">
                                <div className="font-semibold text-slate-800 truncate">{docName}</div>
                                <div className="text-slate-500">
                                  {item.status !== 'completed' && item.status !== 'failed' && item.status !== 'skipped' && item.status !== 'cancelled'
                                    ? `Status: ${item.status}`
                                    : outcome
                                      ? outcome.error
                                        ? `Review could not complete: ${outcome.error}`
                                        : outcome.reason || `Decision: ${outcome.decision}`
                                      : 'No outcome recorded.'}
                                </div>
                                {outcome?.appliedState === 'auto_applied' && (
                                  outcome.decision === 'REJECTED'
                                    ? <div className="font-semibold text-red-600">Auto-rejected by the background review</div>
                                    : <div className="font-semibold text-emerald-600">Auto-approved by the background review</div>
                                )}
                                {outcome?.appliedState === 'skipped_decided' && (
                                  <div className="font-semibold text-slate-500">Skipped — a reviewer decided this document while the job ran</div>
                                )}
                                {outcome?.note && <div className="text-slate-500 italic">{outcome.note}</div>}
                              </div>
                              {outcome && outcome.appliedState === 'pending' && !outcome.error && (
                                <span className="shrink-0 px-1.5 py-px rounded-md text-[10px] font-bold bg-amber-50 text-amber-700 ring-1 ring-amber-200">
                                  Awaiting your decision
                                </span>
                              )}
                            </div>
                          );
                        })
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Main Documents Data */}
      {loading ? (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center shadow-sm">
          <RefreshCw className="w-8 h-8 text-brand animate-spin mx-auto mb-3" />
          <p className="text-sm text-slate-600 font-medium">Loading documents inventory...</p>
        </div>
      ) : error ? (
        <div className="bg-white border border-red-200 rounded-xl p-8 text-center shadow-sm">
          <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-3" />
          <h3 className="text-sm font-bold text-slate-900">Failed to load documents</h3>
          <p className="text-xs text-slate-500 mt-1">{error.safe}</p>
          <details className="mt-3 text-left max-w-lg mx-auto">
            <summary className="text-[11px] text-slate-500 cursor-pointer hover:text-slate-600">Technical details</summary>
            <p className="text-[11px] font-mono text-slate-500 bg-slate-50 border border-slate-200 rounded-lg p-2 mt-1 break-words">{error.raw}</p>
          </details>
          <button
            onClick={() => fetchDocuments()}
            className="mt-4 px-4 py-2 text-xs font-semibold bg-brand text-white rounded-lg hover:bg-brand/90"
          >
            Retry
          </button>
        </div>
      ) : groupByApp ? (
        /* Grouped View — server-paginated by application group */
        groups.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-xl p-12 text-center shadow-sm">
            <FileText className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <h3 className="text-base font-bold text-slate-800">No documents found</h3>
            <p className="text-xs text-slate-500 mt-1 max-w-sm mx-auto">
              No applicant documents match the selected filters or tab queue. Try clearing your search parameters.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {groups.map(group => {
              const state = groupSelectionState(selection, group.documents);
              return (
                <div key={group.applicationId} className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                  <div className="bg-slate-50 px-4 py-3 border-b border-slate-200 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <input
                        type="checkbox"
                        checked={state === 'all'}
                        ref={el => { if (el) el.indeterminate = state === 'some'; }}
                        onChange={e => handleSelectPage(group.documents, e.target.checked)}
                        className="rounded border-slate-300 text-brand focus:ring-brand shrink-0"
                        aria-label={`Select all documents for ${group.applicantName}`}
                      />
                      <span className="text-xs font-bold text-slate-800 truncate">{group.applicantName}</span>
                      <button
                        onClick={() => onSelectApplication(group.applicationId)}
                        className="text-xs text-brand font-semibold hover:underline inline-flex items-center gap-1 shrink-0"
                      >
                        App ID: {group.applicationId}
                        <ExternalLink className="w-3 h-3" />
                      </button>
                    </div>
                    <span className="text-xs font-medium text-slate-500 shrink-0">{group.totalDocuments} document(s)</span>
                  </div>
                  <div className="divide-y divide-slate-100">
                    {group.documents.map(doc => (
                      <div key={doc.docId} className="p-4 flex items-start justify-between gap-3 hover:bg-slate-50">
                        <div className="flex items-start gap-3 min-w-0">
                          <input
                            type="checkbox"
                            checked={selection.has(doc.docId)}
                            onChange={() => handleSelectDoc(doc)}
                            className="rounded border-slate-300 text-brand focus:ring-brand mt-0.5 shrink-0"
                            aria-label={`Select ${doc.documentName}`}
                          />
                          <FileText className="w-4 h-4 text-brand mt-0.5 shrink-0" />
                          <div className="min-w-0">
                            <div className="text-xs font-bold text-slate-900">{doc.documentName}</div>
                            <div className="text-[11px] text-slate-500">{doc.documentType} • Version: {doc.version} • Status: {doc.status}</div>
                            {renderScanInfo(doc)}
                            {renderJobOutcome(doc)}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {renderStatusBadge(doc.systemDecision)}
                          {renderRowActions(doc)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}

            <div>
              <p className="text-xs text-slate-500 mb-1">
                Showing {groups.length} application(s) on this page · {docsOnGroupPage} document(s) shown of {totalDocuments} in the filtered set
              </p>
              <ListPagination
                page={page}
                pageCount={pageCount}
                total={total}
                start={groupStart}
                end={groupEnd}
                pageSize={pageSize}
                onPageChange={setPage}
                onPageSizeChange={size => { setPageSize(size); setPage(1); }}
                itemLabel="applications"
              />
            </div>
          </div>
        )
      ) : documents.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center shadow-sm">
          <FileText className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <h3 className="text-base font-bold text-slate-800">No documents found</h3>
          <p className="text-xs text-slate-500 mt-1 max-w-sm mx-auto">
            {total > 0 && page > 1
              ? 'This page is beyond the current results. Go back to the first page.'
              : 'No applicant documents match the selected filters or tab queue. Try clearing your search parameters.'}
          </p>
          {total > 0 && page > 1 && (
            <button onClick={() => setPage(1)} className="mt-4 px-4 py-2 text-xs font-semibold bg-brand text-white rounded-lg hover:bg-brand/90">
              Back to page 1
            </button>
          )}
        </div>
      ) : (
        /* Flat Table View — server-paginated */
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-[11px] font-bold text-slate-600 uppercase tracking-wider">
                  <th className="py-3 px-4 w-10">
                    <input
                      type="checkbox"
                      checked={pageSelectionState === 'all'}
                      ref={el => { if (el) el.indeterminate = pageSelectionState === 'some'; }}
                      onChange={e => handleSelectPage(documents, e.target.checked)}
                      className="rounded border-slate-300 text-brand focus:ring-brand"
                      aria-label="Select all documents on this page"
                      title="Select all documents on this page"
                    />
                  </th>
                  <th className="py-3 px-4">Applicant Name</th>
                  <th className="py-3 px-4">Program</th>
                  <th className="py-3 px-4">Application ID</th>
                  <th className="py-3 px-4">Document Type</th>
                  <th className="py-3 px-4">Document Name</th>
                  <th className="py-3 px-4">Uploaded Date & Time</th>
                  <th className="py-3 px-4">Uploaded By</th>
                  <th className="py-3 px-4">Version</th>
                  <th className="py-3 px-4">Document Status</th>
                  <th className="py-3 px-4">System Decision</th>
                  <th className="py-3 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 text-xs text-slate-700">
                {documents.map(doc => {
                  const isSelected = selection.has(doc.docId);
                  const isExpanded = expandedRowId === doc.docId;

                  return (
                    <Fragment key={doc.docId}>
                      <tr className={`hover:bg-slate-50/80 transition-colors ${isSelected ? 'bg-brand/5' : ''}`}>
                        <td className="py-3 px-4">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleSelectDoc(doc)}
                            aria-label={`Select ${doc.documentName}`}
                            className="rounded border-slate-300 text-brand focus:ring-brand"
                          />
                        </td>
                        <td className="py-3 px-4 font-semibold text-slate-900">
                          <button
                            onClick={() => onSelectApplication(doc.applicationId)}
                            className="text-left hover:text-brand hover:underline font-semibold"
                          >
                            {doc.applicantName || 'Applicant'}
                          </button>
                          <div className="text-[11px] text-slate-500 font-normal">{doc.applicantEmail}</div>
                        </td>
                        <td className="py-3 px-4 font-medium text-slate-700 max-w-[140px] truncate" title={doc.program}>
                          {doc.program}
                        </td>
                        <td className="py-3 px-4">
                          <button
                            onClick={() => onSelectApplication(doc.applicationId)}
                            className="inline-flex items-center gap-1 font-mono text-xs text-brand font-semibold bg-brand/5 px-2 py-0.5 rounded hover:bg-brand/10 transition-colors"
                          >
                            {doc.applicationId}
                            <ExternalLink className="w-3 h-3" />
                          </button>
                        </td>
                        <td className="py-3 px-4 font-medium text-slate-800">{doc.documentType}</td>
                        <td className="py-3 px-4">
                          <button
                            onClick={() => onPreviewDocument(doc)}
                            className="font-semibold text-slate-900 hover:text-brand hover:underline text-left inline-flex items-center gap-1 max-w-[180px] truncate"
                          >
                            <FileText className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                            <span className="truncate">{doc.documentName}</span>
                          </button>
                          {renderScanInfo(doc)}
                          {renderJobOutcome(doc)}
                        </td>
                        <td className="py-3 px-4 text-slate-500 whitespace-nowrap">
                          {new Date(doc.uploadedAt).toLocaleString(undefined, {
                            dateStyle: 'short',
                            timeStyle: 'short',
                          })}
                        </td>
                        {/* Uploaded By column MUST remain visible, but is NOT filterable */}
                        <td className="py-3 px-4 font-medium text-slate-600 whitespace-nowrap">
                          {doc.uploadedBy}
                        </td>
                        <td className="py-3 px-4 whitespace-nowrap">
                          <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                            doc.superseded ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-700'
                          }`}>
                            {doc.version}
                          </span>
                        </td>
                        <td className="py-3 px-4 font-medium whitespace-nowrap">
                          <span className="px-2 py-0.5 bg-slate-100 text-slate-700 rounded text-[11px]">
                            {doc.status}
                          </span>
                        </td>
                        <td className="py-3 px-4 whitespace-nowrap">
                          {renderStatusBadge(doc.systemDecision)}
                        </td>
                        <td className="py-3 px-4 text-right whitespace-nowrap">
                          <div className="flex items-center justify-end gap-2">
                            {renderRowActions(doc)}
                            <button
                              onClick={() => setExpandedRowId(isExpanded ? null : doc.docId)}
                              className="p-1 text-slate-400 hover:text-slate-600 rounded-lg sm:hidden"
                              aria-label="Toggle details"
                            >
                              {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                            </button>
                          </div>
                        </td>
                      </tr>

                      {/* Mobile Expandable Row Details */}
                      {isExpanded && (
                        <tr className="bg-slate-50/90 border-b border-slate-200">
                          <td colSpan={12} className="p-4 text-xs space-y-2">
                            <div className="font-bold text-slate-800">Decision Rationale / Rationale Details:</div>
                            <div className="p-2.5 bg-white border border-slate-200 rounded-lg text-slate-600 text-[11px]">
                              {doc.decisionDetails || 'No decision rationale recorded.'}
                            </div>
                            <div className="flex items-center justify-between pt-2">
                              <span className="text-slate-500">MIME: {doc.mimeType || 'N/A'} • Size: {doc.fileSize ? `${Math.round(doc.fileSize / 1024)} KB` : 'N/A'}</span>
                              <button
                                onClick={() => onPreviewDocument(doc)}
                                className="text-brand font-bold hover:underline"
                              >
                                View Complete Audit & History →
                              </button>
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="px-4 pb-4">
            <ListPagination
              page={page}
              pageCount={pageCount}
              total={total}
              start={flatStart}
              end={flatEnd}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={size => { setPageSize(size); setPage(1); }}
              itemLabel="documents"
            />
          </div>
        </div>
      )}
    </div>
  );
};
